import { Button, Input, Select } from 'components';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  searchUserByPhone,
  searchUserByPlate,
  normalizePlateResult,
  normalizePhoneResult,
  UnifiedSearchResult,
} from 'shared/services/user-search.service';
import './SearchUser.scss';

type SearchType = 'phone' | 'plate';

export const SearchUser = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<SearchType>('phone');
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<UnifiedSearchResult | null>(null);

  const searchOptions = [
    {
      value: 'phone',
      label: 'Teléfono',
      description: 'Buscar por número de teléfono',
    },
    {
      value: 'plate',
      label: 'Placa',
      description: 'Buscar por placa de vehículo',
    },
  ];

  const validateInput = (value: string, type: SearchType): boolean => {
    if (!value.trim()) {
      setError('Este campo es requerido');
      return false;
    }

    if (type === 'phone') {
      const phoneRegex = /^[\d\s\-+()]+$/;
      if (!phoneRegex.test(value)) {
        setError('Formato de teléfono inválido');
        return false;
      }
    } else if (type === 'plate') {
      const plateRegex = /^[A-Z0-9\s-]+$/i;
      if (!plateRegex.test(value)) {
        setError('Formato de placa inválido');
        return false;
      }
    }

    setError(null);
    return true;
  };

  const handleSearch = async () => {
    if (!validateInput(searchValue, searchType)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      let normalizedResult: UnifiedSearchResult;

      if (searchType === 'phone') {
        const response = await searchUserByPhone(searchValue);
        normalizedResult = normalizePhoneResult(response);
      } else {
        const response = await searchUserByPlate(searchValue);
        normalizedResult = normalizePlateResult(response);
      }

      setResults(normalizedResult);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error al realizar la búsqueda';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    // Limpiar error cuando el usuario empiece a escribir
    if (error) {
      setError(null);
    }
  };

  const handleSelectChange = (value: string | number) => {
    setSearchType(value as SearchType);
    setSearchValue('');
    setError(null);
    setResults(null);
  };

  const handleGoBack = () => {
    navigate('/settings');
  };

  return (
    <div className="search-user">
      <div className="search-user__container">
        <div className="search-user__header">
          <h1 className="search-user__title">Buscar información de cliente</h1>
        </div>

        <div className="search-user__form">
          <div className="search-user__field">
            <Select
              options={searchOptions}
              placeholder="Tipo de búsqueda"
              onClick={handleSelectChange}
              onBlur={() => {
                // No action needed on blur
              }}
              value={searchType}
              isRequired={true}
              name="searchType"
              id="searchType"
              indexSelect={0}
              isDisabled={isLoading}
            />
          </div>

          <div className="search-user__field">
            <Input
              type={searchType === 'phone' ? 'tel' : 'alphanumeric'}
              name="searchValue"
              id="searchValue"
              placeholder={
                searchType === 'phone'
                  ? 'Ingresa el teléfono'
                  : 'Ingresa la placa'
              }
              value={searchValue}
              onChange={handleInputChange}
              errorLabel={error || undefined}
              errorInput={error || undefined}
              isTouched={!!error}
              isRequired={true}
              isDisabled={isLoading}
              textTransform={searchType === 'plate'}
            />
          </div>

          <div className="search-user__actions">
            <Button
              label={isLoading ? 'Buscando...' : 'Buscar'}
              onClick={handleSearch}
              isDisabled={isLoading || !searchValue.trim()}
              typeStyle="rounded"
              template="primary"
              color="primary"
              size="medium"
            />
          </div>
          <div className="search-user__actions">
            <Button
              label="← Volver"
              onClick={handleGoBack}
              typeStyle="rounded"
              template="secondary"
              color="primary"
              size="medium"
            />
          </div>
        </div>

        {isLoading && (
          <div className="search-user__loading">
            <div className="loading-spinner"></div>
            <p>Buscando información...</p>
          </div>
        )}

        {error && (
          <div className="search-user__error">
            <p>{error}</p>
          </div>
        )}

        {results && (
          <div className="search-user__results">
            <h2>Resultados de la búsqueda</h2>
            <div className="results-content">
              <UnifiedResults data={results} searchType={searchType} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const UnifiedResults = ({
  data,
  searchType,
}: {
  data: UnifiedSearchResult;
  searchType: SearchType;
}) => {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const copyToClipboard = async (value: string | number) => {
    try {
      const stringValue = String(value);
      await navigator.clipboard.writeText(stringValue);
      setCopiedValue(stringValue);
      setTimeout(() => setCopiedValue(null), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  return (
    <div className="user-info">
      {!data.exists ? (
        <div className="no-results">
          <p>No se encontró información para esta búsqueda.</p>
        </div>
      ) : (
        <>
          {/* Mostrar información de propiedad solo para búsqueda por placa */}
          {searchType === 'plate' && data.property && (
            <>
              <h3>Información de la Propiedad</h3>
              <div className="info-grid">
                <div className="info-item">
                  <strong>Placa:</strong> {data.property.plate}
                </div>
              </div>
            </>
          )}

          {/* Mostrar propiedades generales para búsqueda por teléfono */}
          {/* {searchType === 'phone' &&
            data.properties &&
            data.properties.length > 0 && (
              <>
                <h3>Propiedades del Cliente</h3>
                <div className="info-grid">
                  {data.properties.map((property, index) => (
                    <div key={index} className="property-item">
                      <div className="info-item long-text">
                        <strong>UID:</strong> {property.uid}
                      </div>
                      <div className="info-item">
                        <strong>Tipo:</strong> {property.type}
                      </div>
                      <div className="info-item">
                        <strong>Estado:</strong> {property.status}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )} */}

          <h3>Información del Cliente</h3>
          <div className="info-grid">
            <div className="info-item">
              <strong>Nombre:</strong> {data.customer.first_name}{' '}
              {data.customer.last_name}
            </div>
            <div className="info-item long-text">
              <strong>Email:</strong>
              <div className="item-with-icon">
                <span className="item-value">{data.customer.email}</span>
                <button
                  className="item-copy-button"
                  onClick={() => copyToClipboard(data.customer.email)}
                  title="Copiar email"
                >
                  {copiedValue === data.customer.email ? (
                    <Check className="icon-check" />
                  ) : (
                    <Copy className="icon-copy" />
                  )}
                </button>
              </div>
            </div>
            <div className="info-item">
              <strong>Teléfono:</strong> {data.customer.phone_number}
            </div>
            <div className="info-item">
              <strong>Código Postal:</strong> {data.customer.zip_code}
            </div>
            <div className="info-item">
              <strong>Empresa:</strong> {data.customer.company}
            </div>
            <div className="info-item">
              <strong>País:</strong> {data.customer.country}
            </div>
            <div className="info-item">
              <strong>Código de referido:</strong> {data.customer.referer}
            </div>
          </div>

          {data.subscriptions && data.subscriptions.length > 0 && (
            <div className="subscriptions-section">
              <h3>Suscripciones</h3>
              {data.subscriptions.map((subscription, index) => (
                <div key={index} className="subscription-item">
                  <div className="subscription-header">
                    <h4>{subscription.name}</h4>
                    <span
                      className={`status-badge status-${subscription.status
                        .toLowerCase()
                        .replace(/\s/g, '-')}`}
                    >
                      {subscription.status}
                    </span>
                  </div>

                  <div className="info-grid">
                    <div className="info-item long-text">
                      <strong>ID de Suscripción:</strong>{' '}
                      {subscription.subscription_id}
                    </div>
                    <div className="info-item">
                      <strong>Recurrencia de pago:</strong>{' '}
                      {subscription.interval}
                    </div>
                    <div className="info-item">
                      <strong>Fecha de Inicio:</strong>{' '}
                      {formatDate(subscription.date_init)}
                    </div>
                    <div className="info-item">
                      <strong>Fecha de Expiración:</strong>{' '}
                      {formatDate(subscription.date_expire)}
                    </div>
                    <div className="info-item">
                      <strong>Fecha Efectiva:</strong>{' '}
                      {formatDate(subscription.date_effective)}
                    </div>
                    <div className="info-item">
                      <strong>Pagos Fallidos:</strong>{' '}
                      {subscription.failed_payments}
                    </div>
                    <div className="info-item">
                      <strong>Tiene Período de Prueba:</strong>{' '}
                      {subscription.has_trial ? 'Sí' : 'No'}
                    </div>
                    {subscription.trial_end && (
                      <div className="info-item">
                        <strong>Fin del Período de Prueba:</strong>{' '}
                        {formatDate(subscription.trial_end)}
                      </div>
                    )}
                    {subscription.date_cancel && (
                      <div className="info-item">
                        <strong>Fecha de Cancelación:</strong>{' '}
                        {formatDate(subscription.date_cancel)}
                      </div>
                    )}
                  </div>

                  {subscription.properties &&
                    subscription.properties.length > 0 && (
                      <div className="subscription-properties">
                        <h5>Propiedades Asociadas:</h5>
                        {subscription.properties.map((property, propIndex) => (
                          <div key={propIndex} className="property-association">
                            {property.type.toLowerCase() !== 'hogar' && (
                              <>
                                <div className="info-item item-with-inline-icon">
                                  <strong>Tablilla:</strong>
                                  <span className="item-value">
                                    {property.plate}
                                  </span>
                                  <button
                                    className="item-copy-button"
                                    onClick={() =>
                                      copyToClipboard(property.plate)
                                    }
                                    title="Copiar tablilla"
                                  >
                                    {copiedValue === property.plate ? (
                                      <Check className="icon-check" />
                                    ) : (
                                      <Copy className="icon-copy" />
                                    )}
                                  </button>
                                </div>
                                <div className="info-item">
                                  <strong>Año:</strong> {property.year}
                                </div>
                              </>
                            )}
                            {property.type.toLowerCase() === 'hogar' && (
                              <>
                                <div className="info-item">
                                  <strong>Dirección 1:</strong>{' '}
                                  {property.address_1}
                                </div>
                                <div className="info-item">
                                  <strong>Zip Code:</strong>{' '}
                                  {property['zip code']}
                                </div>
                              </>
                            )}
                            <div className="info-item">
                              <strong>Tipo:</strong> {property.type}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
